import { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Stars02,
} from "@untitledui/icons"
import { useOrganization } from "@/lib/organization/organization-context"
import { cx } from "@/lib/utils/cx"
import { basicSideOf, type BasicSide } from "../quick-estimate/types"
import { FE_PROCESS_COPY } from "./copy"
import type { FullEstimateInputs } from "./state"
import {
  submitFullEstimateCreate,
  runDamageAgent,
} from "./submit"

// Full Estimate — Step 4 (Generate).
//
// On mount, runs the full-estimate agent pipeline:
//   1. POST /estimates/team/:teamId/full (create + customer/vehicle)
//   2. POST /estimates/:id/images (attach photos)
//   3. POST /estimates/:id/damage (no quickEstimateMode) → ping
// Once the damage job lands, the wizard auto-advances to the damage
// editor. The 5-stage choreography animates over the same timeline so
// the user sees forward progress even on slow agents.
//
// Visually: top-down car silhouette echoing SidePicker; covered
// sides pulse indigo while running, flip emerald when complete.

interface StepProcessingProps {
  inputs: FullEstimateInputs
  onReady: (estimateId: number) => void
  onBack: () => void
}

const STAGE_DURATION_MS = 4000
const TICKER_INTERVAL_MS = 1300

export function StepProcessing({
  inputs,
  onReady,
  onBack,
}: StepProcessingProps) {
  const { activeTeamId } = useOrganization()
  const teamId = activeTeamId

  const [estimateId, setEstimateId] = useState<number | null>(null)
  const [agentsDone, setAgentsDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeStageIdx, setActiveStageIdx] = useState(0)
  const [stagesDone, setStagesDone] = useState(false)
  const startedRef = useRef(false)

  // Sides covered (for the car silhouette highlight).
  const coveredSides = useMemo(() => {
    const s = new Set<BasicSide>()
    for (const p of inputs.photos) {
      if (p.uploadedUrl.length > 0 || p.sendWithoutAiTag) {
        s.add(basicSideOf(p.detectedSide))
      }
    }
    return s
  }, [inputs.photos])

  const submittablePhotos = useMemo(
    () =>
      inputs.photos.filter((p) => p.uploadedUrl.length > 0 || p.sendWithoutAiTag),
    [inputs.photos],
  )

  // 1. On mount: create estimate, attach images, run damage agent,
  //    then major-operations. Both agents are awaited via ping.
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    if (!teamId) {
      setError("No team selected")
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const result = await submitFullEstimateCreate({
          teamId,
          vin: inputs.vin,
          vehicle: inputs.vehicle,
          photos: inputs.photos,
          notes: inputs.notes,
          customer: inputs.customer,
        })
        if (cancelled) return
        setEstimateId(result.estimateId)

        await runDamageAgent(result.estimateId)
        if (cancelled) return
        setAgentsDone(true)
      } catch (err: unknown) {
        if (cancelled) return
        const msg =
          err instanceof Error ? err.message : "Couldn't generate the estimate"
        setError(msg)
      }
    })()

    return () => {
      cancelled = true
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 3. Stage choreography — advance every STAGE_DURATION_MS until
  //    the last stage; then loop the last one until results land.
  useEffect(() => {
    if (error || stagesDone) return
    if (activeStageIdx >= FE_PROCESS_COPY.stages.length - 1) {
      // We're on the last stage; if results are in, complete; if
      // not, hold and let polling unblock us via the next effect.
      const timer = setTimeout(() => setStagesDone(true), STAGE_DURATION_MS)
      return () => clearTimeout(timer)
    }
    const timer = setTimeout(
      () => setActiveStageIdx((i) => i + 1),
      STAGE_DURATION_MS,
    )
    return () => clearTimeout(timer)
  }, [activeStageIdx, error, stagesDone])

  // 4. Ready gate — both choreography done AND agent jobs finished.
  const ready = stagesDone && agentsDone

  // 4b. Auto-advance once ready. Brief delay so the user sees the
  //     final "complete" state before the page transitions; the
  //     wizard's AnimatePresence handles the visual handoff.
  const advancedRef = useRef(false)
  useEffect(() => {
    if (!ready || estimateId === null) return
    if (advancedRef.current) return
    advancedRef.current = true
    const t = window.setTimeout(() => onReady(estimateId), 900)
    return () => window.clearTimeout(t)
  }, [ready, estimateId, onReady])

  // 5. Activity ticker — rotates messages within the active stage.
  const [tickerIdx, setTickerIdx] = useState(0)
  useEffect(() => {
    if (error || ready) return
    setTickerIdx(0)
    const t = setInterval(() => {
      setTickerIdx((i) => i + 1)
    }, TICKER_INTERVAL_MS)
    return () => clearInterval(t)
  }, [activeStageIdx, error, ready])

  const activeStage = FE_PROCESS_COPY.stages[activeStageIdx]
  const tickerMessage =
    activeStage.tickers[tickerIdx % activeStage.tickers.length]

  if (error) {
    return <ErrorPanel message={error} onRetry={onBack} onBack={onBack} />
  }

  return (
    <div className="space-y-6 pb-36 sm:pb-0">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {FE_PROCESS_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {FE_PROCESS_COPY.title}
        </h2>
        <p className="mt-1.5 text-[14px] text-zinc-500 leading-relaxed">
          {FE_PROCESS_COPY.subtitle}
        </p>
      </header>

      {/* Recap pill — read-only context strip. The user can't edit
          here; once the AI run kicks off there's nothing useful to
          do but watch the analysis complete. */}
      <RecapPill
        vehicleLabel={
          [
            inputs.vehicle?.year,
            inputs.vehicle?.make,
            inputs.vehicle?.model,
          ]
            .filter(Boolean)
            .join(" ") || "Vehicle"
        }
        colorHex={inputs.vehicle?.colorHex ?? null}
        photoCount={submittablePhotos.length}
        sidesCovered={coveredSides.size}
      />

      {/* Constellation card — top-down car silhouette with covered
          sides highlighted in waves as stages progress. */}
      <ConstellationCard
        coveredSides={coveredSides}
        activeStageIdx={activeStageIdx}
        ready={ready}
      />

      {/* Stage list. */}
      <StageList activeStageIdx={activeStageIdx} stagesDone={stagesDone} />

      {/* Activity ticker (hidden when ready). */}
      <AnimatePresence>
        {!ready && (
          <motion.div
            key="ticker"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="text-center"
          >
            <p className="text-[11.5px] text-zinc-500 italic">
              <span className="font-semibold uppercase tracking-wider not-italic text-zinc-400 mr-1.5">
                {FE_PROCESS_COPY.tickerPrefix}
              </span>
              {tickerMessage}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status footer — passive. There's no CTA here: when the
          run completes the wizard auto-advances. We surface a
          discreet "ready" or "thinking" pill so the user knows
          where the loop is. Back-link only available while still
          processing (you can't unwind a completed run). */}
      <div
        className={cx(
          "space-y-2",
          "fixed inset-x-0 bottom-0 z-30",
          "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]",
          "bg-gradient-to-t from-white via-white/95 to-white/0",
          "sm:static sm:px-0 sm:pt-2 sm:pb-0 sm:bg-transparent",
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          {ready ? (
            <motion.div
              key="ready"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className={cx(
                "w-full h-14 rounded-2xl flex items-center justify-center gap-2",
                "bg-emerald-50 ring-1 ring-emerald-200 text-emerald-900",
                "text-[13.5px] font-semibold",
              )}
            >
              <Check className="size-4 text-emerald-600" strokeWidth={2.6} aria-hidden />
              {FE_PROCESS_COPY.ready}
            </motion.div>
          ) : (
            <motion.div
              key="thinking"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full h-14 rounded-2xl bg-zinc-100 text-zinc-500 flex items-center justify-center gap-2 text-[13.5px] font-medium"
            >
              <Stars02 className="size-4 animate-pulse text-indigo-500" aria-hidden />
              {stagesDone
                ? FE_PROCESS_COPY.syncing
                : FE_PROCESS_COPY.thinking}
            </motion.div>
          )}
        </AnimatePresence>
        {!ready && (
          <button
            type="button"
            onClick={onBack}
            className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Back
          </button>
        )}
      </div>
    </div>
  )
}

/* ── Recap pill ───────────────────────────────────────────────── */

function RecapPill({
  vehicleLabel,
  colorHex,
  photoCount,
  sidesCovered,
}: {
  vehicleLabel: string
  colorHex: string | null
  photoCount: number
  sidesCovered: number
}) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-3 flex items-center gap-3">
      {colorHex ? (
        <div
          className="size-10 rounded-xl shrink-0 shadow-sm border border-zinc-200/70"
          style={{ background: colorHex }}
          aria-hidden
        />
      ) : (
        <div
          className="size-10 rounded-xl shrink-0 bg-zinc-100 border border-zinc-200/70"
          aria-hidden
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-zinc-900 leading-tight truncate">
          {vehicleLabel}
        </p>
        <p className="mt-0.5 text-[11.5px] leading-tight text-zinc-500">
          <span className="text-zinc-600 font-medium">
            {photoCount === 1
              ? FE_PROCESS_COPY.recapPhotoCountOne
              : FE_PROCESS_COPY.recapPhotoCountMany(photoCount)}
          </span>
          <span className="text-zinc-300 mx-1.5">·</span>
          <span>{FE_PROCESS_COPY.recapSidesCovered(sidesCovered)}</span>
        </p>
      </div>
    </div>
  )
}

/* ── Constellation card ───────────────────────────────────────── */

function ConstellationCard({
  coveredSides,
  activeStageIdx,
  ready,
}: {
  coveredSides: Set<BasicSide>
  activeStageIdx: number
  ready: boolean
}) {
  return (
    <div className="rounded-3xl border border-zinc-200 bg-gradient-to-br from-zinc-50/80 via-white to-emerald-50/40 p-6 md:p-8">
      <div className="flex items-center justify-center">
        <CarSilhouette
          coveredSides={coveredSides}
          activeStageIdx={activeStageIdx}
          ready={ready}
        />
      </div>
    </div>
  )
}

// Top-down car silhouette. 5 section paths corresponding to the
// 5 BasicSide values. Each path lights up if the user took at
// least one photo of that side, sequenced through the stages.
function CarSilhouette({
  coveredSides,
  activeStageIdx,
  ready,
}: {
  coveredSides: Set<BasicSide>
  activeStageIdx: number
  ready: boolean
}) {
  // Stage gating: which stages "wake up" the side overlays. Stage 0
  // (Reading) → no highlight yet; stage 1 (Sections) → start
  // lighting up; stages 2–4 keep them on with a pulse; ready → all
  // covered sides solid + a check.
  const showSides = activeStageIdx >= 1 || ready
  const accentClass = ready
    ? "fill-emerald-500"
    : "fill-indigo-500"

  return (
    <svg
      viewBox="0 0 100 160"
      className="w-44 sm:w-52 h-auto"
      aria-hidden
      fill="none"
    >
      {/* Soft halo behind the body — pulses while AI is thinking. */}
      <motion.circle
        cx="50"
        cy="80"
        r="58"
        fill="url(#halo)"
        animate={
          ready
            ? { opacity: 0.55 }
            : { opacity: [0.2, 0.5, 0.2] }
        }
        transition={
          ready
            ? { duration: 0.4 }
            : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
        }
      />

      <defs>
        <radialGradient id="halo" cx="0.5" cy="0.5" r="0.6">
          <stop
            offset="0%"
            stopColor={ready ? "rgb(16 185 129)" : "rgb(99 102 241)"}
            stopOpacity="0.35"
          />
          <stop
            offset="100%"
            stopColor={ready ? "rgb(16 185 129)" : "rgb(99 102 241)"}
            stopOpacity="0"
          />
        </radialGradient>
      </defs>

      {/* Body */}
      <rect
        x="22"
        y="14"
        width="56"
        height="132"
        rx="18"
        className="fill-white stroke-zinc-300"
        strokeWidth="1"
      />
      {/* Windshield + trunk seams */}
      <line
        x1="28"
        y1="48"
        x2="72"
        y2="48"
        className="stroke-zinc-200"
        strokeWidth="1"
      />
      <line
        x1="28"
        y1="112"
        x2="72"
        y2="112"
        className="stroke-zinc-200"
        strokeWidth="1"
      />
      {/* Center spine */}
      <line
        x1="50"
        y1="48"
        x2="50"
        y2="112"
        className="stroke-zinc-100"
        strokeWidth="0.7"
      />

      {/* Wheels */}
      <rect x="18" y="32" width="6" height="14" rx="1.5" className="fill-zinc-300" />
      <rect x="76" y="32" width="6" height="14" rx="1.5" className="fill-zinc-300" />
      <rect x="18" y="114" width="6" height="14" rx="1.5" className="fill-zinc-300" />
      <rect x="76" y="114" width="6" height="14" rx="1.5" className="fill-zinc-300" />

      {/* Per-side overlays — emerald when ready, indigo while
          processing. Each fades in after stage 1 (Sections). */}
      {showSides && coveredSides.has("FRONT_CENTER") && (
        <SideOverlay
          d="M22 32 V32 a18 18 0 0 1 18 -18 h20 a18 18 0 0 1 18 18 V32 Z M22 32 V14 a18 18 0 0 1 18 -0 h20 a18 18 0 0 1 18 0 V32 Z"
          accentClass={accentClass}
          delay={0.05}
        />
      )}
      {showSides && coveredSides.has("REAR_CENTER") && (
        <SideOverlay
          d="M22 128 V146 a18 18 0 0 0 18 0 h20 a18 18 0 0 0 18 -0 V128 Z"
          accentClass={accentClass}
          delay={0.15}
        />
      )}
      {showSides && coveredSides.has("LEFT_SIDE") && (
        <SideOverlay
          d="M22 32 a18 18 0 0 1 8 -16 V144 a18 18 0 0 1 -8 -16 Z"
          accentClass={accentClass}
          delay={0.25}
        />
      )}
      {showSides && coveredSides.has("RIGHT_SIDE") && (
        <SideOverlay
          d="M78 32 a18 18 0 0 0 -8 -16 V144 a18 18 0 0 0 8 -16 Z"
          accentClass={accentClass}
          delay={0.35}
        />
      )}
      {showSides && coveredSides.has("ROOF") && (
        <SideOverlay
          d="M28 48 H72 V112 H28 Z"
          accentClass={accentClass}
          delay={0.45}
          opacity={0.55}
        />
      )}
    </svg>
  )
}

function SideOverlay({
  d,
  accentClass,
  delay,
  opacity = 0.7,
}: {
  d: string
  accentClass: string
  delay: number
  opacity?: number
}) {
  return (
    <motion.path
      d={d}
      className={accentClass}
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, opacity, opacity * 0.7, opacity] }}
      transition={{
        duration: 2.4,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  )
}

/* ── Stage list ───────────────────────────────────────────────── */

function StageList({
  activeStageIdx,
  stagesDone,
}: {
  activeStageIdx: number
  stagesDone: boolean
}) {
  return (
    <ol className="space-y-1.5" aria-live="polite">
      {FE_PROCESS_COPY.stages.map((stage, idx) => {
        const status: "pending" | "active" | "done" = stagesDone
          ? "done"
          : idx < activeStageIdx
            ? "done"
            : idx === activeStageIdx
              ? "active"
              : "pending"
        return (
          <li key={stage.id}>
            <StageRow label={stage.label} status={status} />
          </li>
        )
      })}
    </ol>
  )
}

function StageRow({
  label,
  status,
}: {
  label: string
  status: "pending" | "active" | "done"
}) {
  return (
    <div
      className={cx(
        "relative rounded-xl px-3.5 py-2.5 flex items-center gap-3 overflow-hidden",
        "transition-colors duration-200",
        status === "active"
          ? "bg-indigo-50/70 border border-indigo-100"
          : status === "done"
            ? "bg-emerald-50/40 border border-emerald-100"
            : "bg-zinc-50 border border-zinc-100",
      )}
    >
      {/* Progress shimmer for active row. */}
      {status === "active" && (
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-100/0 via-indigo-200/40 to-indigo-100/0"
          initial={{ x: "-30%" }}
          animate={{ x: "130%" }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          style={{ width: "30%" }}
          aria-hidden
        />
      )}
      <span
        className={cx(
          "relative size-6 rounded-full grid place-items-center shrink-0 transition-colors duration-200",
          status === "active"
            ? "bg-indigo-500 text-white shadow-sm"
            : status === "done"
              ? "bg-emerald-500 text-white"
              : "bg-zinc-200 text-zinc-400",
        )}
        aria-hidden
      >
        {status === "done" ? (
          <motion.svg
            viewBox="0 0 16 16"
            className="size-3"
            fill="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <motion.path
              d="M3 8 l3 3 7-7"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            />
          </motion.svg>
        ) : status === "active" ? (
          <motion.span
            className="size-1.5 rounded-full bg-white"
            animate={{ scale: [0.8, 1.25, 0.8] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          />
        ) : (
          <span className="size-1.5 rounded-full bg-zinc-400" />
        )}
      </span>
      <span
        className={cx(
          "relative text-[13px] font-semibold leading-tight",
          status === "active"
            ? "text-indigo-900"
            : status === "done"
              ? "text-emerald-900"
              : "text-zinc-500",
        )}
      >
        {label}
      </span>
      {status === "done" && (
        <Check
          className="relative size-4 text-emerald-600 ml-auto shrink-0"
          strokeWidth={2.5}
          aria-hidden
        />
      )}
    </div>
  )
}

/* ── Error panel ──────────────────────────────────────────────── */

function ErrorPanel({
  message,
  onRetry,
  onBack,
}: {
  message: string
  onRetry: () => void
  onBack: () => void
}) {
  return (
    <div className="space-y-6 py-4">
      <div className="rounded-3xl border border-rose-200 bg-rose-50/60 p-6 md:p-8 text-center space-y-4">
        <div className="mx-auto size-12 rounded-full bg-rose-100 grid place-items-center">
          <AlertTriangle
            className="size-5 text-rose-700"
            aria-hidden
            strokeWidth={2.4}
          />
        </div>
        <div className="space-y-1">
          <h2 className="text-[20px] font-bold text-rose-900 tracking-tight">
            {FE_PROCESS_COPY.errorTitle}
          </h2>
          <p className="text-[13.5px] text-rose-800/80 leading-relaxed max-w-md mx-auto">
            {FE_PROCESS_COPY.errorBody}
          </p>
          <p className="text-[11.5px] text-rose-700/70 mt-1.5 font-mono">
            {message}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onRetry}
          className="w-full h-12 rounded-2xl bg-zinc-900 hover:bg-black text-white text-[14px] font-semibold transition-colors duration-150"
        >
          {FE_PROCESS_COPY.errorRetry}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {FE_PROCESS_COPY.errorBack}
        </button>
      </div>
    </div>
  )
}
